import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_BTN_SECONDARY,
  AE_EDITABLE_INPUT,
  AE_ICON_SIZE,
  AE_LABEL,
  AE_MUTED,
  AE_SELECT,
} from '@/components/assessment-engine/layout/aeTheme';
import { BuilderIconButton } from './BuilderIconButton';
import { SCALE_DISPLAY_STYLES, DEFAULT_SCALE_MAX_LABEL, DEFAULT_SCALE_MIN_LABEL, scalePoints } from './scaleUtils';
import { ScaleValueLabelsEditor } from './ScaleInput';
import type { AeTemplateScalePreset } from './templateConfigTypes';

interface ScalePresetsEditorProps {
  presets: AeTemplateScalePreset[];
  readOnly?: boolean;
  onChange: (presets: AeTemplateScalePreset[]) => void;
}

function newPresetId(): string {
  return `scale_${Date.now().toString(36)}`;
}

/** Keep per-value labels aligned when min/max change. */
function resizeValueLabels(labels: string[] | undefined, min: number, max: number): string[] {
  const length = Math.max(0, max - min + 1);
  const next = [...(labels ?? [])].slice(0, length);
  while (next.length < length) next.push('');
  return next;
}

/** Editable template-level scale presets (Setup tab), including custom endpoint and value labels. */
export const ScalePresetsEditor: React.FC<ScalePresetsEditorProps> = ({
  presets,
  readOnly = false,
  onChange,
}) => {
  const { t } = useLanguage();

  const updatePreset = (id: string, patch: Partial<AeTemplateScalePreset>) => {
    onChange(
      presets.map((p) => {
        if (p.id !== id) return p;
        const next = { ...p, ...patch };
        if (patch.scale_min !== undefined || patch.scale_max !== undefined) {
          next.scaleValueLabels = resizeValueLabels(
            next.scaleValueLabels,
            next.scale_min,
            next.scale_max
          );
        }
        return next;
      })
    );
  };

  const addPreset = () => {
    onChange([
      {
        id: newPresetId(),
        name: t('assessmentEngine.builder.newScalePresetDefault'),
        scale_min: 1,
        scale_max: 5,
        allow_na: true,
        matrixShared: false,
        scaleDisplayStyle: 'radio',
        scaleMinLabel: DEFAULT_SCALE_MIN_LABEL,
        scaleMaxLabel: DEFAULT_SCALE_MAX_LABEL,
        scaleValueLabels: scalePoints(1, 5).map(() => ''),
      },
      // Put the new scale first so it is immediately visible and editable,
      // even when the template already has several long shared-scale cards.
      ...presets,
    ]);
  };

  const removePreset = (id: string) => {
    if (presets.length <= 1) return;
    onChange(presets.filter((p) => p.id !== id));
  };

  if (readOnly) {
    return (
      <ul className="space-y-2">
        {presets.map((preset) => (
          <li
            key={preset.id}
            className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-md px-3 py-2"
          >
            <span className="font-medium">{preset.name}</span>
            <span className={AE_MUTED}>
              {preset.scale_min}–{preset.scale_max}
              {preset.allow_na ? ` · ${t('assessmentEngine.builder.allowNa')}` : ''}
              {preset.matrixShared
                ? ` · ${t('assessmentEngine.builder.matrixSharedScale')}`
                : ''}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-3">
      <button type="button" className={AE_BTN_SECONDARY} onClick={addPreset}>
        <PlusIcon className={`${AE_ICON_SIZE} inline mr-1`} aria-hidden />
        {t('assessmentEngine.builder.addScalePreset')}
      </button>
      {presets.map((preset) => (
        <div
          key={preset.id}
          className="space-y-3 border border-[var(--color-border)] rounded-md p-3"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scalePresetName')}</label>
                <input
                  className={AE_EDITABLE_INPUT}
                  value={preset.name}
                  onChange={(e) => updatePreset(preset.id, { name: e.target.value })}
                />
              </div>
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleMin')}</label>
                <input
                  type="number"
                  className={AE_EDITABLE_INPUT}
                  value={preset.scale_min}
                  onChange={(e) => updatePreset(preset.id, { scale_min: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleMax')}</label>
                <input
                  type="number"
                  className={AE_EDITABLE_INPUT}
                  value={preset.scale_max}
                  onChange={(e) => updatePreset(preset.id, { scale_max: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleDisplayStyle')}</label>
                <select
                  className={AE_SELECT}
                  value={preset.scaleDisplayStyle ?? 'numbers'}
                  onChange={(e) =>
                    updatePreset(preset.id, {
                      scaleDisplayStyle: e.target.value as AeTemplateScalePreset['scaleDisplayStyle'],
                    })
                  }
                >
                  {SCALE_DISPLAY_STYLES.map((s) => (
                    <option key={s} value={s}>
                      {t(`assessmentEngine.builder.scaleStyle.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)] sm:pt-6">
                <input
                  type="checkbox"
                  className="accent-[var(--color-accent-bg)]"
                  checked={preset.allow_na}
                  onChange={(e) => updatePreset(preset.id, { allow_na: e.target.checked })}
                />
                {t('assessmentEngine.builder.allowNa')}
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)] sm:pt-6">
                <input
                  type="checkbox"
                  className="accent-[var(--color-accent-bg)]"
                  checked={preset.matrixShared ?? false}
                  onChange={(e) => updatePreset(preset.id, { matrixShared: e.target.checked })}
                />
                {t('assessmentEngine.builder.matrixSharedScale')}
              </label>
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleMinLabel')}</label>
                <input
                  className={AE_EDITABLE_INPUT}
                  value={preset.scaleMinLabel ?? ''}
                  placeholder={DEFAULT_SCALE_MIN_LABEL}
                  onChange={(e) => updatePreset(preset.id, { scaleMinLabel: e.target.value })}
                />
              </div>
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleMaxLabel')}</label>
                <input
                  className={AE_EDITABLE_INPUT}
                  value={preset.scaleMaxLabel ?? ''}
                  placeholder={DEFAULT_SCALE_MAX_LABEL}
                  onChange={(e) => updatePreset(preset.id, { scaleMaxLabel: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <span className={AE_LABEL}>{t('assessmentEngine.builder.scaleValueLabels')}</span>
                <p className={`${AE_MUTED} text-xs mb-1`}>
                  {t('assessmentEngine.builder.scaleValueLabelsHint')}
                </p>
                <ScaleValueLabelsEditor
                  min={preset.scale_min}
                  max={preset.scale_max}
                  labels={preset.scaleValueLabels ?? []}
                  onChange={(scaleValueLabels) => updatePreset(preset.id, { scaleValueLabels })}
                />
              </div>
            </div>
            <BuilderIconButton
              label={t('assessmentEngine.builder.removeScalePreset')}
              onClick={() => removePreset(preset.id)}
              disabled={presets.length <= 1}
            >
              <TrashIcon className={AE_ICON_SIZE} aria-hidden />
            </BuilderIconButton>
          </div>
        </div>
      ))}
    </div>
  );
};
